import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from '@/common/ui/dialog'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	FormDescription,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { Textarea } from '@/common/ui/textarea'
import { Button } from '@/common/ui/button'
import { Alert, AlertDescription } from '@/common/ui/alert'
import { Separator } from '@/common/ui/separator'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { useQueryGetCurrentQuota } from '../../api/hooks/useQueryGetCurrentQuota'
import { formatNumber } from '@/common/lib/utils'
import { InfoIcon, Copy, CheckCircle2 } from 'lucide-react'
import QRCode from 'react-qr-code'
import { toast } from 'sonner'
import api from '@/core/api'

const purchaseQuotaSchema = z.object({
	chain: z.enum(['POLYGON', 'TRC20'], {
		required_error: 'กรุณาเลือก Chain',
	}),
	winningQuotaAmount: z
		.number({ required_error: 'กรุณาระบุจำนวนโควต้า Winning' })
		.min(1, 'จำนวนโควต้าต้องมากกว่า 0'),
	note: z.string().optional(),
})

const confirmPaymentSchema = z.object({
	txHash: z.string().min(1, 'กรุณาระบุ Transaction Hash'),
})

type PurchaseQuotaFormData = z.infer<typeof purchaseQuotaSchema>
type ConfirmPaymentFormData = z.infer<typeof confirmPaymentSchema>

type PaymentResponse = {
	id: string
	address: string
	winningQuotaAmount: number
	creditQuotaAmount: number
	amount: number
	currency: string
	status: string
}

type Props = {
	open: boolean
	onOpenChange: (open: boolean) => void
}

export function PurchaseQuotaDialog({ open, onOpenChange }: Props) {
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [showPaymentDialog, setShowPaymentDialog] = useState(false)
	const [paymentResponse, setPaymentResponse] =
		useState<PaymentResponse | null>(null)
	const { data: currentQuota } = useQueryGetCurrentQuota()

	const form = useForm<PurchaseQuotaFormData>({
		resolver: zodResolver(purchaseQuotaSchema),
		defaultValues: {
			chain: 'POLYGON',
			winningQuotaAmount: undefined,
			note: '',
		},
	})

	const confirmForm = useForm<ConfirmPaymentFormData>({
		resolver: zodResolver(confirmPaymentSchema),
		defaultValues: {
			txHash: '',
		},
	})

	const winningQuotaAmount = form.watch('winningQuotaAmount')
	const selectedChain = form.watch('chain')

	// Amount that will be received (1:100 ratio — server computes creditQuota = winningQuotaAmount * 100)
	const winningQuotaRate = currentQuota?.winningQuotaRatePerUnit ?? 0.06
	const winningQuotaToReceive = winningQuotaAmount || 0
	const creditQuotaToReceive = (winningQuotaAmount || 0) * 100
	const totalCost = (winningQuotaAmount || 0) * winningQuotaRate

	const copyToClipboard = (text: string, label: string) => {
		navigator.clipboard.writeText(text)
		toast.success(`คัดลอก ${label} สำเร็จ`)
	}

	const onSubmit = async (data: PurchaseQuotaFormData) => {
		try {
			setIsSubmitting(true)
			// Call super-server's agent crypto endpoint
			const response = await api.post('/admin/agent/buy-my-quota/crypto', {
				chain: data.chain,
				winningQuotaAmount: data.winningQuotaAmount,
				note: data.note || '',
			})

			setPaymentResponse(response.data)
			setShowPaymentDialog(true)
		} catch {
			toast.error('เกิดข้อผิดพลาดในการสร้างคำขอ')
		} finally {
			setIsSubmitting(false)
		}
	}

	const onConfirmPayment = async (data: ConfirmPaymentFormData) => {
		if (!paymentResponse) return

		try {
			setIsSubmitting(true)
			// Call super-server's confirm crypto endpoint
			await api.post('/admin/agent/buy-my-quota/crypto-confirm', {
				id: paymentResponse.id,
				txHash: data.txHash,
			})

			toast.success('ยืนยันการชำระเงินสำเร็จ')
			setShowPaymentDialog(false)
			setPaymentResponse(null)
			form.reset()
			confirmForm.reset()
			onOpenChange(false)
		} catch {
			toast.error('เกิดข้อผิดพลาดในการยืนยันการชำระเงิน')
		} finally {
			setIsSubmitting(false)
		}
	}

	// Reset form when dialog closes
	useEffect(() => {
		if (!open) {
			form.reset()
			setShowPaymentDialog(false)
			setPaymentResponse(null)
		}
	}, [open, form])

	return (
		<>
			<Dialog open={open} onOpenChange={onOpenChange}>
				<DialogContent className="max-w-lg">
					<DialogHeader>
						<DialogTitle>ซื้อเครดิต (Purchase Credit)</DialogTitle>
						<DialogDescription>
							กรอกข้อมูลเพื่อซื้อโควต้าเพิ่มเติมผ่าน Crypto
						</DialogDescription>
					</DialogHeader>

					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
							{/* Current Balance Info */}
							<Alert>
								<InfoIcon className="h-4 w-4" />
								<AlertDescription>
									<div className="space-y-1 text-xs">
										<div className="flex justify-between">
											<span className="text-muted-foreground">
												โควต้าคงเหลือ:
											</span>
											<span className="font-medium">
												Winning:{' '}
												{formatNumber(
													(currentQuota?.winningQuota ?? 0) -
														(currentQuota?.winningQuotaUsed ?? 0),
												)}{' '}
												| Credit:{' '}
												{formatNumber(
													(currentQuota?.creditQuota ?? 0) -
														(currentQuota?.creditQuotaUsed ?? 0),
												)}
											</span>
										</div>
									</div>
								</AlertDescription>
							</Alert>

							<Separator />

							{/* Purchase Amount Section */}
							<div className="space-y-3">
								<h3 className="text-sm font-semibold">จำนวนโควตา</h3>

								<FormField
									control={form.control}
									name="winningQuotaAmount"
									render={({ field }) => (
										<FormItem>
											<FormLabel required>โควตายอดได้/เสีย *</FormLabel>
											<FormControl>
												<Input
													type="number"
													placeholder="5,000"
													onNumberChange={(value) => field.onChange(value)}
													value={field.value ?? ''}
												/>
											</FormControl>
											{winningQuotaAmount > 0 && (
												<FormDescription className="text-xs">
													= {formatNumber(totalCost)} บาท (
													{formatNumber(winningQuotaAmount)} ×{' '}
													{winningQuotaRate}) | Credit ที่ได้รับ:{' '}
													{formatNumber(winningQuotaAmount * 100)} (
													{formatNumber(winningQuotaAmount)} × 100)
												</FormDescription>
											)}
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<Separator />

							{/* Blockchain Network Section */}
							<div className="space-y-3">
								<h3 className="text-sm font-semibold">ข้อมูล Blockchain</h3>

								<FormField
									control={form.control}
									name="chain"
									render={({ field }) => (
										<FormItem>
											<FormLabel required>เลือก Blockchain Network *</FormLabel>
											<Select
												onValueChange={field.onChange}
												defaultValue={field.value}
											>
												<FormControl>
													<SelectTrigger>
														<SelectValue placeholder="เลือก Chain" />
													</SelectTrigger>
												</FormControl>
												<SelectContent>
													<SelectItem value="POLYGON">
														<div className="flex items-center gap-2">
															Polygon Network
														</div>
													</SelectItem>
													<SelectItem value="TRC20">
														<div className="flex items-center gap-2">
															TRON (Tron Network)
														</div>
													</SelectItem>
												</SelectContent>
											</Select>
											<FormDescription className="text-xs">
												เลือก Blockchain Network สำหรับการชำระเงิน
											</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<Separator />

							{/* Note Section */}
							<FormField
								control={form.control}
								name="note"
								render={({ field }) => (
									<FormItem>
										<FormLabel>หมายเหตุ (ไม่บังคับ)</FormLabel>
										<FormControl>
											<Textarea
												placeholder="กรอกหมายเหตุเพิ่มเติม"
												rows={3}
												{...field}
												value={field.value ?? ''}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<Separator />

							{/* Summary Section */}
							<Card className="bg-muted/50">
								<CardContent className="pt-6">
									<div className="space-y-2">
										<div className="flex items-center justify-between">
											<span className="font-semibold">ยอดรวมทั้งหมด:</span>
											<span className="text-2xl font-bold text-primary">
												{formatNumber(totalCost)} บาท
											</span>
										</div>
										{winningQuotaAmount > 0 && (
											<>
												<p className="text-sm text-muted-foreground">
													สำหรับ Winning Quota{' '}
													{formatNumber(winningQuotaAmount)} หน่วย
												</p>
												<Separator className="my-2" />
												<div className="space-y-1.5 text-xs">
													<div className="flex justify-between">
														<span className="text-muted-foreground">
															Winning Quota ที่จะได้รับ:
														</span>
														<span className="font-semibold text-green-600">
															+{formatNumber(winningQuotaToReceive)} บาท
														</span>
													</div>
													<div className="flex justify-between">
														<span className="text-muted-foreground">
															Credit Quota ที่จะได้รับ:
														</span>
														<span className="font-semibold text-blue-600">
															+{formatNumber(creditQuotaToReceive)} บาท
														</span>
													</div>
												</div>
											</>
										)}
									</div>
								</CardContent>
							</Card>

							{/* Action Buttons */}
							<div className="flex justify-end gap-2 pt-2">
								<Button
									type="button"
									variant="outline"
									onClick={() => onOpenChange(false)}
									disabled={isSubmitting}
								>
									ยกเลิก
								</Button>
								<Button
									type="submit"
									disabled={isSubmitting || !winningQuotaAmount}
									className="w-full"
								>
									{isSubmitting ? 'กำลังส่งคำขอ...' : 'สร้างคำขอชำระเงิน'}
								</Button>
							</div>
						</form>
					</Form>
				</DialogContent>
			</Dialog>

			{/* Payment QR Code Dialog */}
			<Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
				<DialogContent className="flex max-h-[90vh] max-w-md flex-col">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							<CheckCircle2 className="h-5 w-5 text-green-500" />
							รายละเอียดการชำระเงินด้วย Crypto
						</DialogTitle>
						<DialogDescription>
							โอนเหรียญไปที่ Address ด้านล่างและยืนยันด้วย Transaction Hash
						</DialogDescription>
					</DialogHeader>

					{paymentResponse && (
						<div className="space-y-4 overflow-y-auto pr-2">
							<Card>
								<CardHeader>
									<CardTitle className="text-center text-sm">
										สแกน QR Code สำหรับ Wallet Address
									</CardTitle>
								</CardHeader>
								<CardContent className="flex justify-center">
									<div className="rounded-lg border-2 border-muted p-4">
										<QRCode value={paymentResponse.address} size={200} />
									</div>
								</CardContent>
							</Card>

							<div className="space-y-3 rounded-lg bg-muted p-4">
								<div className="space-y-1">
									<p className="text-sm text-muted-foreground">
										Transaction ID
									</p>
									<div className="flex items-center justify-between gap-2">
										<code className="text-sm font-medium">
											{paymentResponse.id}
										</code>
										<Button
											size="sm"
											variant="ghost"
											onClick={() =>
												copyToClipboard(paymentResponse.id, 'Transaction ID')
											}
										>
											<Copy className="h-4 w-4" />
										</Button>
									</div>
								</div>

								<div className="space-y-1">
									<p className="text-sm text-muted-foreground">
										Blockchain Network
									</p>
									<p className="text-sm font-medium">
										{selectedChain === 'POLYGON'
											? 'Polygon Network'
											: 'TRON (Tron Network)'}
									</p>
								</div>

								<div className="space-y-1">
									<p className="text-sm text-muted-foreground">
										Wallet Address
									</p>
									<div className="flex items-center justify-between gap-2">
										<code className="flex-1 overflow-hidden text-ellipsis text-sm font-medium">
											{paymentResponse.address}
										</code>
										<Button
											size="sm"
											variant="ghost"
											onClick={() =>
												copyToClipboard(paymentResponse.address, 'Address')
											}
										>
											<Copy className="h-4 w-4" />
										</Button>
									</div>
								</div>

								<div className="space-y-1">
									<p className="text-sm text-muted-foreground">
										จำนวนเครดิตที่เติม
									</p>
									<p className="text-lg font-bold">
										{formatNumber(paymentResponse.winningQuotaAmount)} Credits
									</p>
								</div>

								<div className="space-y-1">
									<p className="text-sm text-muted-foreground">
										จำนวนเงินที่ต้องชำระ
									</p>
									<p className="text-lg font-bold">
										{formatNumber(paymentResponse.amount)}{' '}
										{paymentResponse.currency}
									</p>
								</div>

								<div className="space-y-1">
									<p className="text-sm text-muted-foreground">สถานะ</p>
									<p className="text-sm font-medium text-amber-600">
										{paymentResponse.status}
									</p>
								</div>
							</div>

							<div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950">
								<p className="mb-2 text-sm font-semibold text-amber-900 dark:text-amber-100">
									ขั้นตอนการชำระเงิน:
								</p>
								<ol className="list-inside list-decimal space-y-1 text-sm text-amber-800 dark:text-amber-200">
									<li>โอนเหรียญไปที่ Address ด้านบน</li>
									<li>รอให้ Transaction ได้รับการยืนยัน</li>
									<li>คัดลอก Transaction Hash</li>
									<li>กรอกและยืนยันด้านล่าง</li>
								</ol>
							</div>

							<Form {...confirmForm}>
								<form
									onSubmit={confirmForm.handleSubmit(onConfirmPayment)}
									className="space-y-4"
								>
									<FormField
										control={confirmForm.control}
										name="txHash"
										render={({ field }) => (
											<FormItem>
												<FormLabel required>Transaction Hash</FormLabel>
												<FormControl>
													<Input
														placeholder="กรอก Transaction Hash"
														{...field}
													/>
												</FormControl>
												<FormDescription className="text-xs">
													กรอก Hash จากการโอนเหรียญเพื่อยืนยัน
												</FormDescription>
												<FormMessage />
											</FormItem>
										)}
									/>

									<Button
										type="submit"
										disabled={isSubmitting}
										className="w-full"
									>
										{isSubmitting ? 'กำลังยืนยัน...' : 'ยืนยันการชำระเงิน'}
									</Button>
								</form>
							</Form>
						</div>
					)}
				</DialogContent>
			</Dialog>
		</>
	)
}
